import statusService, { ICreateStatus } from "api/statusService";
import uiSettingsService from "api/uiSettingsService";
import Input from "components/Input";
import StatusBadge, { getStatusColor } from "components/StatusBadge";
import { IStatus, StatusType } from "dto/IStatus";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { EViews } from "../ClassificationTab/typings";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, SelectChangeEvent, Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheck,
  faXmark,
  faBan,
  faHourglass,
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faPlay,
  faPause,
  faForwardStep,
  faFlag,
  faQuestion,
  faArrowRight,
  faStar,
  faLock,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

// ── Kolekcja ikon do wyboru ──────────────────────────────────────────

const ICON_COLLECTION: { name: string; icon: IconDefinition; label: string }[] = [
  { name: "clock",                 icon: faClock,                 label: "Oczekuje"        },
  { name: "hourglass",             icon: faHourglass,             label: "W toku"          },
  { name: "play",                  icon: faPlay,                  label: "W realizacji"    },
  { name: "pause",                 icon: faPause,                 label: "Wstrzymane"      },
  { name: "forward-step",          icon: faForwardStep,           label: "Przekazane"      },
  { name: "check",                 icon: faCheck,                 label: "Zatwierdzone"    },
  { name: "circle-check",          icon: faCircleCheck,           label: "Potwierdzone"    },
  { name: "xmark",                 icon: faXmark,                 label: "Odrzucone"       },
  { name: "circle-xmark",          icon: faCircleXmark,           label: "Anulowane"       },
  { name: "ban",                   icon: faBan,                   label: "Zablokowane"     },
  { name: "triangle-exclamation",  icon: faTriangleExclamation,   label: "Ostrzeżenie"     },
  { name: "flag",                  icon: faFlag,                  label: "Oflagowane"      },
  { name: "question",              icon: faQuestion,              label: "Do wyjaśnienia"  },
  { name: "arrow-right",           icon: faArrowRight,            label: "Dalej"           },
  { name: "star",                  icon: faStar,                  label: "Ważne"           },
  { name: "lock",                  icon: faLock,                  label: "Zamknięte"       },
];

const iconByName = (name: string | undefined): IconDefinition | null => {
  if (!name) return null;
  return ICON_COLLECTION.find((i) => i.name === name)?.icon ?? null;
};

// ── Selektor kolorów ─────────────────────────────────────────────────

const StatusColorSelect: FC<{
  selectedColor: number;
  setSelectedColor(val: number): void;
}> = ({ selectedColor, setSelectedColor }) => {
  const handleColorChanged = (event: SelectChangeEvent) => {
    setSelectedColor(event.target.value as unknown as number);
  };

  return (
    <Select
      value={selectedColor}
      displayEmpty
      onChange={handleColorChanged}
      disableUnderline
      variant="standard"
      MenuProps={{ style: { borderRadius: "8px" } }}
      inputProps={{ sx: { "&:focus": { background: "none" }, padding: "0" } }}
    >
      {[...Array(15).keys()].map((i) => (
        <MenuItem className={styles.menuItem} value={i} key={"itm" + i}>
          <div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_54775_65726" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
                <rect width="20" height="20" fill="white" />
              </mask>
              <g mask="url(#mask0_54775_65726)">
                <path d="M5 21V4H14L14.4 6H20V16H13L12.6 14H7V21H5Z" fill={getStatusColor(i)} />
              </g>
            </svg>
          </div>
        </MenuItem>
      ))}
    </Select>
  );
};

// ── Demo-statusy dla pustych zakładek ────────────────────────────────

const DEMO_STATUSES: Record<string, IStatus[]> = {
  [EViews.CASES]: [
    { Id: -101, Name: "Nowa",       Color: 9  },
    { Id: -102, Name: "W toku",     Color: 1  },
    { Id: -103, Name: "Oczekuje",   Color: 4  },
    { Id: -104, Name: "Zamknięta",  Color: 7  },
    { Id: -105, Name: "Archiwalna", Color: 5  },
  ],
  [EViews.ACTIONS]: [
    { Id: -201, Name: "Do zrobienia",  Color: 9  },
    { Id: -202, Name: "W realizacji",  Color: 1  },
    { Id: -203, Name: "Wstrzymane",    Color: 4  },
    { Id: -204, Name: "Zakończone",    Color: 6  },
    { Id: -205, Name: "Anulowane",     Color: 7  },
  ],
  [EViews.LETTERS]: [
    { Id: -301, Name: "Robocze",     Color: 9  },
    { Id: -302, Name: "Gotowe",      Color: 1  },
    { Id: -303, Name: "Wysłane",     Color: 6  },
    { Id: -304, Name: "Odebrane",    Color: 3  },
    { Id: -305, Name: "Archiwalne",  Color: 5  },
  ],
  [EViews.PACKAGES_INCOMING]: [
    { Id: -401, Name: "Oczekuje",       Color: 9  },
    { Id: -402, Name: "Odebrane",       Color: 1  },
    { Id: -403, Name: "W opracowaniu",  Color: 4  },
    { Id: -404, Name: "Przekazane",     Color: 3  },
    { Id: -405, Name: "Zakończone",     Color: 6  },
  ],
  [EViews.PACKAGES_OUTCOMING]: [
    { Id: -501, Name: "Przygotowanie",  Color: 9  },
    { Id: -502, Name: "Gotowe",         Color: 1  },
    { Id: -503, Name: "Wysłane",        Color: 3  },
    { Id: -504, Name: "Dostarczone",    Color: 6  },
    { Id: -505, Name: "Zwrócone",       Color: 7  },
  ],
  [EViews.INVOICES]: [
    { Id: -601, Name: "Wystawiona",   Color: 9  },
    { Id: -602, Name: "Wysłana",      Color: 1  },
    { Id: -603, Name: "Opłacona",     Color: 6  },
    { Id: -604, Name: "Po terminie",  Color: 11 },
    { Id: -605, Name: "Anulowana",    Color: 7  },
  ],
  [EViews.ENGAGEMENTS]: [
    { Id: -701, Name: "Do akceptacji", Color: 4  },
    { Id: -702, Name: "Zaakceptowane", Color: 6  },
    { Id: -703, Name: "Do poprawy",    Color: 11 },
    { Id: -704, Name: "Rozliczone",    Color: 3  },
    { Id: -705, Name: "Anulowane",     Color: 7  },
  ],
};

// ── Główny komponent ─────────────────────────────────────────────────

const ClassificationDefaultStatus: FC<Props> = ({ myPrivilages, type }) => {
  const { t } = useTranslation();
  const workspaceId = getWorkspaceId();

  const [systemValue, setSystemValue] = useState<string>();
  const [tagValue, setTagValue] = useState<string>();
  const [statusValue, setStatusValue] = useState<number>(0);

  const { data: systemStatusesData, refetch: systemRefetchStatuses, isSuccess: sysLoaded } =
    statusService.useGetStatuses(workspaceId!, statusValue);
  const { data: statusesData, refetch: refetchStatuses, isSuccess: userLoaded } =
    statusService.useGetStatuses(undefined, statusValue);
  const { mutate: deleteStatus } = statusService.useDeleteStatus();
  const { mutate: createStatus } = statusService.useCreateStatus();
  const { mutate: updateStatus } = statusService.useUpdateStatus();

  const statuses      = useMemo(() => statusesData?.data      ?? [], [statusesData]);
  const systemStatuses = useMemo(() => systemStatusesData?.data ?? [], [systemStatusesData]);

  // ── Konfiguracja wyświetlania (BE: /ui/status-display/{view}) ─────
  // BE jest źródłem prawdy; localStorage to warstwa podręczna hydratowana niżej.
  const viewStatusIds = useMemo(() => {
    const ids = new Set<number>();
    for (const s of statuses) ids.add(s.Id as number);
    for (const s of systemStatuses) ids.add(s.Id as number);
    return ids;
  }, [statuses, systemStatuses]);

  const { data: beStatusConfig } = uiSettingsService.useGetStatusDisplay(String(type));
  const saveStatusDisplay = uiSettingsService.useSaveStatusDisplay();

  // Zapisuje do BE konfigurację bieżącego widoku (ikony/nadpisania zawężone do
  // statusów tego widoku — globalne mapy localStorage nie nakładają się między widokami).
  const pushStatusConfig = (
    mode: "chip" | "icon",
    icons: Record<number, string>,
    overrides: Record<number, { Name: string; Color: number }>,
  ) => {
    const Icons = Object.entries(icons)
      .filter(([id]) => viewStatusIds.has(Number(id)))
      .map(([id, IconName]) => ({ StatusId: Number(id), IconName }));
    const Overrides = Object.entries(overrides)
      .filter(([id]) => viewStatusIds.has(Number(id)))
      .map(([id, ov]) => ({ StatusId: Number(id), Name: ov.Name, Color: ov.Color }));
    saveStatusDisplay.mutate({ View: String(type), Mode: mode, Icons, Overrides });
  };

  // ── Tryb prezentacji i ikony (localStorage) ──────────────────────
  const [displayMode, setDisplayMode] = useState<"chip" | "icon">(() => {
    try {
      return (JSON.parse(localStorage.getItem("status_display_mode_v1") || "{}")[type] ?? "chip") as "chip" | "icon";
    } catch { return "chip"; }
  });

  const [iconMap, setIconMap] = useState<Record<number, string>>(() => {
    try { return JSON.parse(localStorage.getItem("status_icons_v1") || "{}"); }
    catch { return {}; }
  });

  // ── Tooltip (oddzielny od nazwy chipa) ───────────────────────────
  const [tooltipMap, setTooltipMap] = useState<Record<number, string>>(() => {
    try { return JSON.parse(localStorage.getItem("status_tooltips_v1") || "{}"); }
    catch { return {}; }
  });
  const [editingTooltipId, setEditingTooltipId] = useState<number | null>(null);
  const [tooltipDraft, setTooltipDraft] = useState("");

  // ── Kolejność wierszy (DnD, per typ) ────────────────────────────
  const getOrderForType = (t: string): number[] => {
    try {
      const stored = JSON.parse(localStorage.getItem("status_order_v1") || "{}");
      return Array.isArray(stored[t]) ? stored[t] : [];
    } catch { return []; }
  };

  const [orderedIds, setOrderedIds] = useState<number[]>(() => getOrderForType(type));

  useEffect(() => {
    setOrderedIds(getOrderForType(type));
  }, [type]);

  const [openIconPickerId, setOpenIconPickerId] = useState<number | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const saveDisplayMode = (mode: "chip" | "icon") => {
    setDisplayMode(mode);
    try {
      const stored = JSON.parse(localStorage.getItem("status_display_mode_v1") || "{}");
      stored[type] = mode;
      localStorage.setItem("status_display_mode_v1", JSON.stringify(stored));
    } catch {}
    pushStatusConfig(mode, iconMap, chipOverrides);
  };

  const saveIcon = (statusId: number, iconName: string) => {
    setIconMap((prev) => {
      const next = { ...prev, [statusId]: iconName };
      localStorage.setItem("status_icons_v1", JSON.stringify(next));
      pushStatusConfig(displayMode, next, chipOverrides);
      return next;
    });
    setOpenIconPickerId(null);
  };

  // ── Edycja Chip (nazwa + kolor) ──────────────────────────────────
  const [editingChipId, setEditingChipId] = useState<number | null>(null);
  const [chipDraft, setChipDraft] = useState("");
  const [chipColorDraft, setChipColorDraft] = useState(0);

  const [chipOverrides, setChipOverrides] = useState<Record<number, { Name: string; Color: number }>>(() => {
    try { return JSON.parse(localStorage.getItem("status_chip_overrides_v1") || "{}"); }
    catch { return {}; }
  });

  // Hydratacja stanu i warstwy podręcznej (localStorage) z konfiguracji BE.
  useEffect(() => {
    if (!beStatusConfig) return;
    const mode = beStatusConfig.Mode === "icon" ? "icon" : "chip";
    setDisplayMode(mode);
    try {
      const stored = JSON.parse(localStorage.getItem("status_display_mode_v1") || "{}");
      stored[type] = mode;
      localStorage.setItem("status_display_mode_v1", JSON.stringify(stored));
    } catch { /* localStorage niedostępny — pomijamy hydratację cache */ }

    setIconMap((prev) => {
      const next = { ...prev };
      for (const i of beStatusConfig.Icons ?? []) next[i.StatusId] = i.IconName;
      try { localStorage.setItem("status_icons_v1", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });

    setChipOverrides((prev) => {
      const next = { ...prev };
      for (const o of beStatusConfig.Overrides ?? []) next[o.StatusId] = { Name: o.Name, Color: o.Color };
      try { localStorage.setItem("status_chip_overrides_v1", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, [beStatusConfig, type]);

  const startEditChip = (s: IStatus) => {
    if (isDemo(s)) return;
    setEditingChipId(s.Id as number);
    setChipDraft(chipOverrides[s.Id as number]?.Name ?? s.Name);
    setChipColorDraft(chipOverrides[s.Id as number]?.Color ?? s.Color);
  };

  const saveChip = (s: IStatus) => {
    if (!chipDraft.trim()) return;
    const name = chipDraft.trim();
    const color = chipColorDraft;
    setChipOverrides((prev) => {
      const next = { ...prev, [s.Id as number]: { Name: name, Color: color } };
      localStorage.setItem("status_chip_overrides_v1", JSON.stringify(next));
      pushStatusConfig(displayMode, iconMap, next);
      return next;
    });
    setTooltipMap((prev) => {
      const next = { ...prev, [s.Id as number]: name };
      localStorage.setItem("status_tooltips_v1", JSON.stringify(next));
      return next;
    });
    setEditingChipId(null);
    updateStatus(
      { ...s, Name: name, Color: color },
      {
        onSuccess: () => {
          refetchStatuses();
          systemRefetchStatuses();
        },
      },
    );
  };

  const cancelChip = () => setEditingChipId(null);

  const startEditTooltip = (s: IStatus) => {
    setEditingTooltipId(s.Id);
    setTooltipDraft(tooltipMap[s.Id] ?? s.Name);
  };

  const saveTooltip = (statusId: number) => {
    setTooltipMap((prev) => {
      const next = { ...prev, [statusId]: tooltipDraft };
      localStorage.setItem("status_tooltips_v1", JSON.stringify(next));
      return next;
    });
    setEditingTooltipId(null);
  };

  const cancelTooltip = () => setEditingTooltipId(null);

  // zamknij picker po kliknięciu poza nim
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setOpenIconPickerId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    switch (type) {
      case EViews.CASES:           setStatusValue(StatusType.CASES);          return;
      case EViews.ACTIONS:         setStatusValue(StatusType.TASKS);          return;
      case EViews.CLIENTS:         setStatusValue(StatusType.PARTICIPANTS);   return;
      case EViews.LETTERS:         setStatusValue(StatusType.EDITORDOCUMENTS);return;
      case EViews.PACKAGES_INCOMING: setStatusValue(StatusType.INCOMING);    return;
      case EViews.PACKAGES_OUTCOMING: setStatusValue(StatusType.OUTGOING);   return;
      case EViews.TEMPLATES:       setStatusValue(StatusType.TEMPLATES);      return;
      case EViews.INVOICES:        setStatusValue(StatusType.INVOICES);       return;
      case EViews.ENGAGEMENTS:     setStatusValue(StatusType.ENGAGEMENTS);    return;
      default:                     setStatusValue(null);                       return;
    }
  }, [type]);

  const handleTagChange = (text: string) => setTagValue(text);
  const [, setShowDropdown] = useState(-1);
  const [selectedSystemColor, setSelectedSystemColor] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);

  const isDemo = (s: IStatus) => (s.Id as number) < 0;

  const [deleteConfirmStatus, setDeleteConfirmStatus] = useState<IStatus | null>(null);

  const removeStatus = (status: IStatus) => {
    if (isDemo(status)) return;
    setShowDropdown(-1);
    deleteStatus({ Id: status.Id as number }, {
      onSuccess: () => {
        setChipOverrides((prev) => {
          const next = { ...prev };
          delete next[status.Id as number];
          localStorage.setItem("status_chip_overrides_v1", JSON.stringify(next));
          return next;
        });
        refetchStatuses();
        systemRefetchStatuses();
      },
    });
    setDeleteConfirmStatus(null);
  };

  const addStatus = (isCompany: boolean, color: number) => {
    let payload: ICreateStatus = { Name: tagValue, Color: color, Type: statusValue };
    if (isCompany) {
      if (!systemValue) return;
      payload = { Name: systemValue, Color: color, Type: statusValue, WorkspaceId: workspaceId ?? "" };
      setSystemValue("");
    } else {
      if (!tagValue) return;
      setTagValue("");
    }
    createStatus(payload, {
      onSuccess: () => { refetchStatuses(); systemRefetchStatuses(); },
    });
  };

  // ── Sortowanie wierszy ────────────────────────────────────────────
  const allStatuses = useMemo(() => {
    const seen = new Set<number>();
    const dedup = (items: IStatus[]) =>
      items.filter((s) => {
        const id = s.Id as number;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    const real = dedup([
      ...systemStatuses,
      ...statuses.filter((s) => !s.WorkspaceId && !s.Global),
    ]);
    if (sysLoaded && userLoaded && real.length === 0) {
      return DEMO_STATUSES[type] ?? [];
    }
    return real;
  }, [systemStatuses, statuses, sysLoaded, userLoaded, type]);

  const sortedStatuses = useMemo(() => {
    const base = (() => {
      if (orderedIds.length === 0) return allStatuses;
      const indexed = new Map(allStatuses.map((s) => [s.Id, s]));
      const ordered = orderedIds.map((id) => indexed.get(id)).filter(Boolean) as IStatus[];
      const rest = allStatuses.filter((s) => !orderedIds.includes(s.Id));
      return [...ordered, ...rest];
    })();
    return base.map((s) => {
      const ov = chipOverrides[s.Id as number];
      return ov ? { ...s, Name: ov.Name, Color: ov.Color } : s;
    });
  }, [allStatuses, orderedIds, chipOverrides]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(sortedStatuses);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const newOrder = items.map((s) => s.Id);
    setOrderedIds(newOrder);
    try {
      const stored = JSON.parse(localStorage.getItem("status_order_v1") || "{}");
      stored[type] = newOrder;
      localStorage.setItem("status_order_v1", JSON.stringify(stored));
    } catch {}
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className={styles.wrapper}>

      {/* Przełącznik trybu prezentacji */}
      <div className={styles.modeToggle}>
        <span className={styles.modeLabel}>Wybierz sposób prezentacji statusu:</span>
        <button
          className={`${styles.modeBtn} ${displayMode === "chip" ? styles.modeBtnActive : ""}`}
          onClick={() => saveDisplayMode("chip")}
        >
          Chip
        </button>
        <button
          className={`${styles.modeBtn} ${displayMode === "icon" ? styles.modeBtnActive : ""}`}
          onClick={() => saveDisplayMode("icon")}
        >
          Ikona
        </button>
      </div>

      {/* Tabela statusów */}
      <div className={styles.statusTable}>

        {/* Nagłówek */}
        <div className={styles.tableHeader}>
          <span className={styles.colDrag} />
          <span className={styles.colChip}>Chip</span>
          <span className={styles.colIcon}>Ikona</span>
          <span className={styles.colTooltip}>
            Tooltip
            {sysLoaded && userLoaded && allStatuses.length > 0 && isDemo(allStatuses[0]) && (
              <span className={styles.demoBadge}>przykładowe</span>
            )}
          </span>
        </div>

        {/* Wiersze statusów z DnD */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="status-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {sortedStatuses.map((s, index) => {
                  const currentIcon = iconByName(iconMap[s.Id]);
                  const color = getStatusColor(s.Color);
                  const isPickerOpen = openIconPickerId === s.Id;
                  const isEditingTooltip = editingTooltipId === s.Id;
                  const tooltipText = tooltipMap[s.Id] ?? s.Name;

                  return (
                    <Draggable key={s.Id} draggableId={String(s.Id)} index={index}>
                      {(drag, snapshot) => (
                        <div
                          ref={drag.innerRef}
                          {...drag.draggableProps}
                          className={`${styles.tableRow} ${snapshot.isDragging ? styles.tableRowDragging : ""}`}
                        >
                          {/* Drag handle */}
                          <span className={styles.colDrag} {...drag.dragHandleProps}>
                            <FontAwesomeIcon icon={faGripVertical} className={styles.dragHandle} />
                          </span>

                          {/* Kolumna 1: Chip */}
                          <span className={styles.colChip}>
                            {editingChipId === s.Id ? (
                              <div className={styles.chipEditWrap}>
                                <StatusColorSelect
                                  selectedColor={chipColorDraft}
                                  setSelectedColor={setChipColorDraft}
                                />
                                <input
                                  autoFocus
                                  className={styles.chipInput}
                                  value={chipDraft}
                                  onChange={(e) => setChipDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveChip(s);
                                    if (e.key === "Escape") cancelChip();
                                  }}
                                />
                                <button className={styles.tooltipSaveBtn} onClick={() => saveChip(s)}>✓</button>
                                <button className={styles.tooltipCancelBtn} onClick={cancelChip}>✕</button>
                              </div>
                            ) : (
                              <span
                                className={`${styles.chipClickable} ${isDemo(s) ? styles.chipDemo : ""}`}
                                onClick={() => startEditChip(s)}
                                title={isDemo(s) ? undefined : "Kliknij aby edytować"}
                              >
                                <StatusBadge status={s} showName onRemove={(!s.Global && !isDemo(s)) ? () => setDeleteConfirmStatus(s) : null} />
                              </span>
                            )}
                          </span>

                          {/* Kolumna 2: Ikona */}
                          <span className={styles.colIcon}>
                            <div className={styles.iconPickerWrap}>
                              <Tooltip title={currentIcon ? ICON_COLLECTION.find((i) => i.name === iconMap[s.Id])?.label ?? "" : "Wybierz ikonę"}>
                                <button
                                  className={`${styles.iconPickerBtn} ${!currentIcon ? styles.iconPickerBtnEmpty : ""}`}
                                  style={currentIcon ? { color } : undefined}
                                  onClick={() => setOpenIconPickerId(isPickerOpen ? null : s.Id)}
                                >
                                  {currentIcon
                                    ? <FontAwesomeIcon icon={currentIcon} />
                                    : <span className={styles.iconPickerPlaceholder}>+</span>
                                  }
                                </button>
                              </Tooltip>

                              {isPickerOpen && (
                                <div className={styles.iconPickerPopover} ref={pickerRef}>
                                  {ICON_COLLECTION.map((item) => (
                                    <Tooltip key={item.name} title={item.label} placement="top">
                                      <button
                                        className={`${styles.iconOption} ${iconMap[s.Id] === item.name ? styles.iconOptionSelected : ""}`}
                                        style={iconMap[s.Id] === item.name ? { color, background: color + "18" } : undefined}
                                        onClick={() => saveIcon(s.Id, item.name)}
                                      >
                                        <FontAwesomeIcon icon={item.icon} />
                                      </button>
                                    </Tooltip>
                                  ))}
                                </div>
                              )}
                            </div>
                          </span>

                          {/* Kolumna 3: Tooltip (edytowalny, niezależny od nazwy chipa) */}
                          <span className={styles.colTooltip}>
                            {isEditingTooltip ? (
                              <div className={styles.tooltipEditWrap}>
                                <input
                                  autoFocus
                                  className={styles.tooltipInput}
                                  value={tooltipDraft}
                                  onChange={(e) => setTooltipDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") saveTooltip(s.Id);
                                    if (e.key === "Escape") cancelTooltip();
                                  }}
                                />
                                <button className={styles.tooltipSaveBtn} onClick={() => saveTooltip(s.Id)}>✓</button>
                                <button className={styles.tooltipCancelBtn} onClick={cancelTooltip}>✕</button>
                              </div>
                            ) : (
                              <span
                                className={styles.tooltipText}
                                onClick={() => startEditTooltip(s)}
                                title="Kliknij aby edytować"
                              >
                                {tooltipText}
                              </span>
                            )}
                          </span>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Wiersz dodawania nowego statusu */}
        <div className={styles.tableAddRow}>
          <span className={styles.colDrag} />
          <span className={styles.colChip}>
            <Input
              LeftIcon={
                <StatusColorSelect
                  selectedColor={selectedColor}
                  setSelectedColor={setSelectedColor}
                />
              }
              type="text"
              onChange={handleTagChange}
              onAccept={() => addStatus(false, selectedColor)}
              label={t("common.status")}
              onRightText={t("common.save")}
              onRightClick={() => addStatus(false, selectedColor)}
              value={tagValue}
              hideTags
            />
          </span>
          <span className={styles.colIcon} />
          <span className={styles.colTooltip} />
        </div>

      </div>

      {/* Dialog potwierdzenia usunięcia statusu */}
      <Dialog
        open={!!deleteConfirmStatus}
        onClose={() => setDeleteConfirmStatus(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "16px" }}>
          Usuń status
        </DialogTitle>
        <DialogContent>
          <p style={{ margin: "0 0 12px", fontSize: "14px", color: "rgba(0,0,0,0.75)" }}>
            Czy na pewno chcesz usunąć status <strong>„{deleteConfirmStatus?.Name}"</strong>?
          </p>
          <p style={{ margin: "0 0 12px", fontSize: "14px", color: "rgba(0,0,0,0.75)" }}>
            Status zostanie <strong>trwale usunięty</strong> i zniknie ze wszystkich miejsc,
            w których jest obecnie przypisany — dotyczy to spraw, działań, dokumentów
            i innych obiektów korzystających z tego statusu.
          </p>
          <p style={{
            margin: 0,
            fontSize: "13px",
            color: "#5d49c7",
            background: "rgba(93,73,199,0.07)",
            borderRadius: "6px",
            padding: "10px 14px",
            lineHeight: "1.5",
          }}>
            <strong>Wskazówka:</strong> zamiast usuwać status, rozważ zmianę jego etykiety —
            kliknij na chip, aby edytować nazwę i kolor. Istniejące przypisania zostaną zachowane.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px", gap: "8px" }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmStatus(null)}
            sx={{ textTransform: "none" }}
          >
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirmStatus && removeStatus(deleteConfirmStatus)}
            sx={{ textTransform: "none" }}
          >
            Usuń permanentnie
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClassificationDefaultStatus;
